import React from 'react';
import { Badge, Space, Tag, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../../../components';
import { QueueItem } from '../hooks/useQueueMonitor';

const { Text } = Typography;

const statusColors: Record<string, string> = {
  active: 'success',
  paused: 'warning',
  stalled: 'error',
};

interface QueueListProps {
  queues: QueueItem[];
  loading: boolean;
  onRefresh: () => void;
  onViewJobs: (queue: QueueItem) => void;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
}

export const QueueList: React.FC<QueueListProps> = ({
  queues,
  loading,
  onRefresh,
  onViewJobs,
  pagination,
}) => {
  const columns: ColumnsType<QueueItem> = [
    {
      title: 'Queue Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={statusColors[status] || 'default'}>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'Jobs',
      key: 'jobs',
      width: 240,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Space size="middle">
            <Badge status="default" text={<Text style={{ fontSize: 12 }}>Queued: {record.job_counts.queued}</Text>} />
            <Badge status="processing" text={<Text style={{ fontSize: 12 }}>Running: {record.job_counts.running}</Text>} />
          </Space>
          <Space size="middle">
            <Badge status="success" text={<Text style={{ fontSize: 12 }}>Done: {record.job_counts.completed}</Text>} />
            <Badge status="error" text={<Text style={{ fontSize: 12 }}>Failed: {record.job_counts.failed}</Text>} />
          </Space>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <EyeOutlined
          style={{ fontSize: 18, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onViewJobs(record);
          }}
        />
      ),
    },
  ];

  return (
    <DataTable
      title="Queues"
      data={queues}
      columns={columns}
      loading={loading}
      rowKey="id"
      onRefresh={onRefresh}
      emptyText="No queues found"
      onRowClick={onViewJobs}
      pagination={pagination}
    />
  );
};